import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtDuration, fmtNumber, fmtRelative } from "@/lib/format";
import {
  ErrorState,
  GapList,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

export default function SourceHealthPage() {
  const q = useQuery({ queryKey: ["source-health"], queryFn: dashApi.sourceHealth, refetchInterval: 30_000 });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Source Health"
        description="Connector status, freshness and last-run outcome for every integration."
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            {Object.entries(q.data?.summary ?? {}).map(([k, v]) => (
              <Stat key={k} label={k.replace("_", " ")} value={fmtNumber(v)} />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {q.data?.sources.map((s) => (
              <Panel
                key={s.code}
                title={
                  <span className="flex items-center gap-2">
                    <SourceBadge code={s.code} /> {s.name}
                  </span>
                }
                right={<StatusPill status={s.status} />}
              >
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Transport" value={<span className="text-sm">{s.transport}</span>} />
                  <Stat label="Rows" value={fmtNumber(s.rows)} />
                  <Stat label="Last run" value={<span className="text-sm">{fmtDuration(s.last_run_duration_ms)}</span>} sub={s.last_run_status ?? "—"} />
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Last good data {s.captured_at ? fmtRelative(s.captured_at) : "never"} ·
                  Last attempt {s.last_run_at ? fmtRelative(s.last_run_at) : "never"}
                  {s.failure_kind && s.failure_kind !== "none" && (
                    <span className="ml-1 text-amber-300">· reason: {s.failure_kind.replace("_", " ")}</span>
                  )}
                </p>
                {s.network && (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="text-slate-500">Network</span>
                    <span className="font-mono text-slate-400">{s.network.required_network}</span>
                    {!s.network.docker_available ? (
                      <StatusPill status="no_data" label="unknown" />
                    ) : s.network.attached ? (
                      <StatusPill status="ok" label="attached" />
                    ) : (
                      <StatusPill status="down" label={s.network.network_exists ? "not attached" : "network missing"} />
                    )}
                  </div>
                )}
                {s.last_error && (
                  <p className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 font-mono text-[11px] text-rose-300">
                    {s.last_error}
                  </p>
                )}
                {s.recommended_fix && (
                  <p className="mt-2 rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-200">
                    <span className="font-semibold">Fix:</span> {s.recommended_fix}
                  </p>
                )}
                {s.gaps.length > 0 && (
                  <div className="mt-3">
                    <GapList gaps={s.gaps} />
                  </div>
                )}
              </Panel>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
